import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Box } from '@mui/material';
import { useSubscriptionsPlansData } from 'constants/subscriptions';
import { SubscriptionPlansLayout } from 'layouts/SubscriptionPlansLayout';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getIsSubscriptionLoading, getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useToggle } from 'hooks/useToggler';

import { SUBSCRIPTION_RECURRENT } from 'interfaces/subscriptions';

import { Loader } from 'components/_basic/Loader/Loader';
import { useAuthModalsContext } from 'components/AuthModals';
import { CancelSubscriptionModal } from 'components/CancelSubscriptionModal/CancelSubscriptionModal';
import { PlanCard } from 'components/PlanCard/PlanCard';
import { CustomizedSwitches } from 'components/Switch/Switch';

import { formatDate } from 'helpers/formatDates';
import ToastService from 'helpers/ToastService';

import styles from '../SubscriptionsPlansPage/SubscriptionsPlansPage.module.scss';

export const OnlyPayedSubscriptionsPlansPage = () => {
  const [isYearlySubscription, setIsYearlySubscription] = useState(false);
  const [isCancelModalOpened, setIsCancelModalOpened] = useToggle(false);

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isSubscriptionLoading = useUserSubscriptionStore(getIsSubscriptionLoading);

  const allSubscriptionsPlans = useSubscriptionsPlansData();
  const payedSubscriptions = allSubscriptionsPlans.slice(1);

  const handleSwitchSubscription = (value: boolean) => {
    setIsYearlySubscription(value);
  };

  const handleCancelSubscription = () => {
    setIsCancelModalOpened();
  };
  const { setIsModalOpened } = useAuthModalsContext();

  useEffect(() => {
    setIsModalOpened(false);
  }, []);

  // Fix showing toast after stripe payment
  useEffect(() => {
    if (userSubscription?.nextSubscription.recurrent === SUBSCRIPTION_RECURRENT.MONTHLY) {
      ToastService.showSuccess(
        `Your "${userSubscription.nextSubscription.subscriptionPlan}" monthly subscription starts on ${formatDate(userSubscription.currentSubscription.endDate)}`,
        `Your "${userSubscription.currentSubscription.subscriptionPlan}" annual subscription is cancelled`,
        {
          autoClose: 3000,
          className: styles.toast,
        },
      );
    }
  }, [userSubscription?.nextSubscription?.recurrent]);

  if (isSubscriptionLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
        <script type="text/javascript" defer>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                          })(window,document,'script','dataLayer','GTM-PB57NZW9');`}
        </script>
        <script type="text/javascript" defer>
          {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '951508482576405');
            fbq('track', 'PageView');`}
        </script>
      </Helmet>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
        <Box marginBottom={{ xs: '24px', md: '32px' }}>
          <CustomizedSwitches switcherValue={isYearlySubscription} onChange={handleSwitchSubscription} />
        </Box>
        <SubscriptionPlansLayout>
          {payedSubscriptions.map((subscriptionsPlan) => (
            <PlanCard
              key={subscriptionsPlan.tariff}
              isYearly={isYearlySubscription}
              subscriptionData={subscriptionsPlan}
              onCancelSubscription={handleCancelSubscription}
            />
          ))}
        </SubscriptionPlansLayout>
        {userSubscription && (
          <CancelSubscriptionModal
            isOpened={isCancelModalOpened}
            handleClose={setIsCancelModalOpened}
            userSubscription={userSubscription}
          />
        )}
      </Box>
    </>
  );
};
